import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();

  const currentCategory = categories.find((c) => c.slug === slug);
  const activeSlug = slug || categories[0].slug;
  const displayCategory = currentCategory || categories[0];
  const categoryArticles = getArticlesByCategory(activeSlug);

  if (!currentCategory && slug) {
    // Fallback for wrong slug
    const fallbackCategory = categories[0];
    const fallbackArticles = getArticlesByCategory(fallbackCategory.slug);
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 pb-14 min-h-screen">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: '分类浏览' },
            ]} />
            <div className="text-center py-20">
              <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">分类未找到</h1>
              <p className="text-foreground-500 mb-6">该分类不存在或已被移除</p>
              <Link to={`/category/${fallbackCategory.slug}`} className="text-primary-600 hover:text-primary-700 font-medium">
                返回{fallbackCategory.name}
              </Link>
            </div>
            <div className="mt-10">
              <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-4">推荐阅读</h2>
              <div className="space-y-4">
                {fallbackArticles.slice(0, 3).map((a) => (
                  <ArticleCard key={a.id} article={a} variant="horizontal" />
                ))}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 pb-14 min-h-screen bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: displayCategory.name },
          ]} />

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${displayCategory.icon} text-lg text-primary-600`}></i>
                </div>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {displayCategory.name}
              </h1>
            </div>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
              {displayCategory.description}
            </p>
          </header>

          {/* Category Tabs */}
          <div className="mb-8">
            <div className="flex overflow-x-auto pb-2 gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Article List */}
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-background-100 rounded-xl">
              <div className="w-14 h-14 flex items-center justify-center mx-auto rounded-full bg-background-200 mb-4">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}